import { useQuery } from "@tanstack/react-query"
import { first } from "lodash"

import apiClient from "@api/client"
import { QUERY_KEYS } from "@api/constants"
import { ONE_MINUTE } from "@constants/time"
import { Menu } from "@features/menu"

import { useActiveStore } from "./useActiveStore"
import { useCart } from "providers/cart-provider"
import { useMap } from "@features/maps/hooks/use-map"

export const getActiveMenuQuery = ({
  storeId = null,
  omitMenuOptions = true,
  scheduledOrderTime = null,
  scheduledOrderDate = null,
}: {
  storeId: number | null
  omitMenuOptions?: boolean
  scheduledOrderTime?: string | null
  scheduledOrderDate?: string | null
}) => ({
  enabled: storeId != null,
  queryKey: [
    QUERY_KEYS.GET_ACTIVE_MENU,
    storeId,
    omitMenuOptions,
    scheduledOrderTime,
    scheduledOrderDate,
  ],
  // Invalidates the query to refetch the menu because of item changes
  staleTime: ONE_MINUTE,
  queryFn: async () => {
    let url = `stores/${storeId}/menus/?omit_menu_options=${!!omitMenuOptions}`
    if (scheduledOrderTime && scheduledOrderDate) {
      url += `&scheduled_time=${scheduledOrderTime}&scheduled_date=${scheduledOrderDate}`
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await apiClient.get<any[]>(url)

    // TODO: probably change at some point
    const rawMenu = first(response?.body ?? [])
    return rawMenu ?? null
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  select: (data: { [key: string]: any } | null) => {
    return data ? Menu.fromDynamic(data) : null
  },
})

export const useActiveMenu = () => {
  const { activeStore } = useActiveStore()
  const { selectedStore } = useMap()
  const { cart } = useCart()

  let scheduledDate: string | undefined = undefined
  let scheduledTime: string | undefined = undefined

  if (cart.data != null) {
    if (
      cart.data!.scheduledStartTime != null &&
      cart.data!.scheduledDayjs != null
    ) {
      const year = cart.data!.scheduledDayjs!.year()
      const month = cart.data!.scheduledDayjs!.month() + 1
      const day = cart.data!.scheduledDayjs!.date()
      scheduledDate = `${year}-${month < 10 ? `0${month}` : month}-${day < 10 ? `0${day}` : day}`
      scheduledTime = cart.data!.scheduledStartTime
    }
  }

  const storeId = selectedStore ?? activeStore?.id ?? null

  return useQuery(
    getActiveMenuQuery({
      storeId,
      scheduledOrderDate: scheduledDate,
      scheduledOrderTime: scheduledTime,
    }),
  )
}
